import React, { useCallback, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import HeadingItem from '@atlaskit/menu/heading-item';
import LinkItem from '@atlaskit/menu/link-item';
import Section from '@atlaskit/menu/section';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	getDevDocsSourceProviderConfigFromValue,
	getRepositoryPartsFromPathParts,
} from '../source-resolution/devDocsSourceProviders.ts';

import { DevDocsProviderIconTile, getDevDocsProviderDisplayName } from './DevDocsProviderIcon.tsx';
import type { DevDocsBylineConnection } from './devDocsConnections.ts';

const i18n = defineMessages({
	syncedWith: {
		id: 'dev-docs.byline-connection.menu.section.synced-with.ai-non-final',
		defaultMessage: 'Synced with ({count, number})',
		description:
			'Section heading in the Teamwork Graph byline menu for content synced from a Dev Docs source. The count is the number of synced sources shown in the section.',
	},
	providerRepo: {
		id: 'dev-docs.byline-connection.menu.section.provider-repo.ai-non-final',
		defaultMessage: '{provider} repo',
		description:
			'Metadata label in the Teamwork Graph byline menu for a Dev Docs source repository. The provider placeholder is the source control provider name, such as GitHub or Bitbucket.',
	},
});

const styles = cssMap({
	rowWrapper: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
	textColumn: {
		minWidth: 0,
		flex: 1,
	},
	title: {
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	metadata: {
		minWidth: 0,
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text.subtlest'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	metadataPart: {
		minWidth: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	metadataSeparator: {
		display: 'inline-block',
		flexShrink: 0,
		width: '3px',
		height: '3px',
		marginInline: token('space.050'),
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.bold'),
	},
});

type FormatRelativeTime = IntlShape['formatRelativeTime'];
type RelativeTimeUnit = NonNullable<Parameters<FormatRelativeTime>[1]>;

const formatSyncedTime = (
	timestamp: string | null,
	formatRelativeTime: FormatRelativeTime,
): string | null => {
	if (!timestamp) {
		return null;
	}

	const timestampMs = new Date(timestamp).getTime();
	if (Number.isNaN(timestampMs)) {
		return null;
	}

	const elapsedSeconds = Math.round((Date.now() - timestampMs) / 1000);
	const absoluteSeconds = Math.abs(elapsedSeconds);
	const formatElapsedTime = (unitSizeInSeconds: number, unit: RelativeTimeUnit) =>
		formatRelativeTime(-Math.round(elapsedSeconds / unitSizeInSeconds), unit);

	if (absoluteSeconds < 60) {
		return formatElapsedTime(1, 'second');
	}
	if (absoluteSeconds < 60 * 60) {
		return formatElapsedTime(60, 'minute');
	}
	if (absoluteSeconds < 60 * 60 * 24) {
		return formatElapsedTime(60 * 60, 'hour');
	}
	if (absoluteSeconds < 60 * 60 * 24 * 7) {
		return formatElapsedTime(60 * 60 * 24, 'day');
	}
	if (absoluteSeconds < 60 * 60 * 24 * 30) {
		return formatElapsedTime(60 * 60 * 24 * 7, 'week');
	}
	if (absoluteSeconds < 60 * 60 * 24 * 365) {
		return formatElapsedTime(60 * 60 * 24 * 30, 'month');
	}

	return formatElapsedTime(60 * 60 * 24 * 365, 'year');
};

type LinkItemWithInteractionProps = React.ComponentProps<typeof LinkItem> &
	Pick<
		React.AnchorHTMLAttributes<HTMLAnchorElement>,
		'onBlur' | 'onFocus' | 'onMouseEnter' | 'onMouseLeave'
	>;

const LinkItemWithInteraction = LinkItem as React.ComponentType<LinkItemWithInteractionProps>;

export const DevDocsBylineConnectionMenuSection = ({
	connection,
	onNavigate,
}: {
	connection: DevDocsBylineConnection;
	onNavigate?: () => void;
}): React.JSX.Element => {
	const { formatMessage, formatRelativeTime } = useIntl();
	const { environment } = useSessionData();
	const [isRepositoryLinkIconVisible, setIsRepositoryLinkIconVisible] = useState(false);
	const lastSynced = formatSyncedTime(connection.lastSynced, formatRelativeTime);
	const providerDisplayName = getDevDocsProviderDisplayName(connection);
	const providerConfig = getDevDocsSourceProviderConfigFromValue(connection.provider);
	const repositoryParts = getRepositoryPartsFromPathParts(connection.repoName.split('/'));
	const repositoryUrl =
		providerConfig && repositoryParts
			? providerConfig.buildCanonicalUrl(repositoryParts, { environment })
			: null;
	const providerTypeLabel = providerDisplayName
		? formatMessage(i18n.providerRepo, { provider: providerDisplayName })
		: null;
	const metadataParts = [providerTypeLabel, connection.owner, lastSynced].filter(
		(metadataPart): metadataPart is string => Boolean(metadataPart),
	);
	const metadata = metadataParts.length ? (
		<Inline as="span" alignBlock="center" xcss={styles.metadata}>
			{metadataParts.map((metadataPart, index) => (
				<React.Fragment key={`${metadataPart}-${index}`}>
					{index > 0 ? <Box as="span" xcss={styles.metadataSeparator} aria-hidden="true" /> : null}
					<Box as="span" xcss={styles.metadataPart}>
						{metadataPart}
					</Box>
				</React.Fragment>
			))}
		</Inline>
	) : null;
	const rowText = (
		<Stack xcss={styles.textColumn} space="space.025">
			<Box xcss={styles.title}>{connection.repoName}</Box>
			{metadata}
		</Stack>
	);
	const showRepositoryLinkIcon = useCallback(() => {
		setIsRepositoryLinkIconVisible(true);
	}, []);
	const hideRepositoryLinkIcon = useCallback(() => {
		setIsRepositoryLinkIconVisible(false);
	}, []);

	return (
		<Section>
			<HeadingItem>{formatMessage(i18n.syncedWith, { count: 1 })}</HeadingItem>
			{repositoryUrl ? (
				<LinkItemWithInteraction
					href={repositoryUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onNavigate}
					onMouseEnter={showRepositoryLinkIcon}
					onMouseLeave={hideRepositoryLinkIcon}
					onFocus={showRepositoryLinkIcon}
					onBlur={hideRepositoryLinkIcon}
					iconBefore={<DevDocsProviderIconTile connection={connection} />}
					iconAfter={
						isRepositoryLinkIconVisible ? (
							<GrowDiagonalIcon
								label=""
								color={token('color.icon.subtle')}
								testId="dev-docs-byline-repository-link-icon"
							/>
						) : null
					}
				>
					{rowText}
				</LinkItemWithInteraction>
			) : (
				<Box xcss={styles.rowWrapper}>
					<Inline space="space.150" alignBlock="center">
						<DevDocsProviderIconTile connection={connection} />
						{rowText}
					</Inline>
				</Box>
			)}
		</Section>
	);
};
